import Color from 'color';
import styled from 'styled-components/macro';
import { Flex, Box, ClickableBox, Tooltip } from 'components/ui';
import { useNotesPlaying } from 'hooks/useNotesPlaying';
import synthIcon from 'img/synth.svg';
import stopIcon from 'img/stop-icon.svg';
import playIcon from 'img/play-icon.svg';
import { AUDIO_OPTIONS } from '../constants';
import { getColorVariant, FUTURA_CLIP_LAYER_ICONS } from 'utils/futura';

const Cell = styled(Flex)`
  .overlay {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.5);
    display: none;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    text-align: center;
    padding-top: 16px;
    cursor: pointer;
  }

  &:hover {
    .overlay {
      display: block;
    }
  }
`;

const EBF = window.EulerBeatsFutura;

const Section = ({
  layer,
  specReader,
  palette,
  width,
  notesPlaying,
  onClipClick
}) => {
  return (
    <Flex
      width="100%"
      minWidth="600px"
      maxWidth="800px"
      background="black"
      minHeight="80px"
      borderRadius="16px"
      alignCenter
      mb={3}
    >
      <Flex
        column
        alignCenter
        justifyCenter
        width={width}
        minHeight="74px"
        borderRight="1px solid #333"
        color="#999"
        fontSize="12px"
      >
        <Box>
          <img src={ FUTURA_CLIP_LAYER_ICONS[layer.name] || synthIcon } alt={layer.name} width="24px" />
        </Box>
        <Box>{ layer.name }</Box>
      </Flex>
      {
        layer.sections.map((section) => {
          const pickedVariantId =
            specReader.mixCode.pickValues[section.layer.index][section.index];
          const pickedVariantIndex = section.variants.findIndex(item => item.id === pickedVariantId);
          const color = palette.layers[layer.index % palette.layers.length];
          const noteColor = getColorVariant(color, pickedVariantIndex);
          const variant = specReader.getVariant(pickedVariantId);
          const isNone = variant.name === 'None';
          const isPlaying = !isNone && notesPlaying.hasNotePlaying(
            section.index,
            null,
            variant.index,
            layer.index
          );
          const bgColor = isPlaying ? noteColor : 'transparent';
          const isLight = Color(bgColor).isLight();
          const variantChannel = variant.channels[variant.channels.length - 1];
          const channel = specReader.getChannel(variantChannel.channel);
          const clip = variantChannel.clips[variantChannel.clips.length - 1];

          return (
            <Flex
              alignCenter
              justifyCenter
              fontSize="12px"
              width={width}
              height="74px"
              key={`${section.index}-${variant.name}`}
              borderRight={isPlaying ? 'none' : '1px solid #333'}
              p={1}
            >
              <Cell
                width="calc(100% - 2px)"
                margin="0 auto"
                height="68px"
                px={2}
                textAlign="left"
                alignCenter
                justifyCenter
                backgroundColor={bgColor}
                borderBottom={isNone ? 'none' : `8px solid ${noteColor}`}
                fontWeight={isPlaying ? 'bold' : 'normal'}
                position="relative"
              >
                <Box width="100%" color={isLight ? 'black' : 'white'}>
                  { isNone ? '-' : variant.name }
                </Box>

                {!isNone && !!clip && !!channel && (
                  <div
                    onClick={() => {
                      if (!isPlaying) {
                        onClipClick(channel, section, variant, clip);
                      } else { 
                        EBF.visual.stop()
                      }
                    }}
                    className="overlay"
                  >
                    <img src={isPlaying ? stopIcon : playIcon} alt="play" width="32px" />
                  </div>
                )}
              </Cell>
            </Flex>
          );
        })
      }
    </Flex>
  );
}

const SectionHeader = styled(ClickableBox)`
  &: hover {
    background-color: black;

    & > div {
      color: white !important;
    }
  }
`;

const FuturaThemeStructure = ({ elementId, pieceData, palette }) => {
  const { pieceSpec: spec } = pieceData.options;
  const { piece: specReader } = pieceData;
  const sectionWidth = 100/(spec.sections.length);
  const notesPlaying = useNotesPlaying();

  const jumpToSection = (sectionIndex) => {
    EBF.visual.stop();
    notesPlaying.clearNotesPlaying();
    const start = pieceData.piece.sectionStart(sectionIndex);
    EBF.visual.play(elementId, start * pieceData.wholeDuration, true);
  }

  const playClip = async (channel, layerSection, variant, clip) => {
    EBF.visual.stop();
    notesPlaying.clearNotesPlaying();
    const pick = {
      layer: layerSection.layer,
      section: layerSection.section,
      layerSection,
      variant,
    };
    const start = pieceData.piece.pickClipStart(pick, clip);
    let notes = pieceData.piece.notes
      .filter(
        n =>
          n.context.clip.index === clip.index &&
          n.context.variant.index === pick.variant.index &&
          n.context.section.index === pick.section.index &&
          n.context.layer.index === pick.layer.index
      )
      .map(n => ({ ...n, start: n.start - start }));

    if (notes.length === 0) {
      notes = pieceData.piece.clipNotes({ ...clip, start: 0 }, channel, pick, 0);
    }
    if (notes.length === 0) {
      console.warn({ notes });
      return;
    }
    const lastNote = notes[notes.length - 1];
    const totalDuration = (lastNote.start + lastNote.duration) * pieceData.wholeDuration;

    const noteSequence = EBF.utils.toNoteSequence(notes, totalDuration, pieceData.wholeDuration);
    await EBF.audio.prepareSequence(noteSequence, pieceData, AUDIO_OPTIONS, false, false);
    EBF.audio.start();
  }

  return (
    <Box
      overflowX="scroll"
    >
      <Flex
        mt={4}
        width="100%"
        minWidth="600px"
        maxWidth="800px"
      >
        <Box width={sectionWidth + '%'}></Box>
        {
          spec.sections.map(({ name, index }) => {
            const isPlaying = notesPlaying.hasNotePlaying(index)

            return (
              <SectionHeader
                key={name}
                width={sectionWidth + '%'}
                minHeight="40px"
                fontSize="14px"
                textAlign="center"
                borderRadius="8px"
                background={isPlaying ? '#ddd' : '#222'}
                onClick={() => jumpToSection(index)}
                px={2}
                py={2}
                mb={1}
              >
                <Box
                  data-tip="sectiontip"
                  data-for={`tooltip_${name}`}
                  color={isPlaying ? 'black' : '#999'}
                >
                  { name }
                </Box>
                <Tooltip id={`tooltip_${name}`} place="top" effect="solid">
                  Click to play { name } section
                </Tooltip>
              </SectionHeader>
            )
          })
        }
      </Flex>
      {
        spec.layers.map((layer, i) => (
          <Section
            key={i}
            layer={layer}
            specReader={specReader}
            palette={palette}
            notesPlaying={notesPlaying}
            width={sectionWidth + '%'}
            onClipClick={playClip}
          />
        ))
      }
    </Box>
  )
}

export default FuturaThemeStructure;
